"use client";

import React from "react";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { Flame, Skull } from "lucide-react";
import PolymarketMarketCard from "@/app/components/PolymarketMarketCard";
import { useT } from "@/hooks/useT";
import { fmtCents } from "@/lib/format-price";
import { decodeHTMLEntities } from "@/lib/sanitize";
import { formatUSD, timeAgo } from "../../helpers/format";
import { CARD_BASE } from "../../helpers/styles";
import { getPostAttribution } from "../../helpers/post-attribution";
import { EVENT_STYLES } from "../../event-styles";
import type { ActivityEvent } from "../../types";

// Default Row — fallback for any event_type without a dedicated Row.
// Today this catches: pnl_win, pnl_loss, follow, resolution,
// leaderboard_move, streak. JSX byte-identical to the inline default
// branch pulled out of EventCard.
export default function DefaultRow({
  event,
  onVote,
}: {
  event: ActivityEvent;
  onVote: (eventId: string, voteType: number, sourceTable: string) => void;
}) {
  const t = useT();
  const router = useRouter();

  const style = EVENT_STYLES[event.event_type] || EVENT_STYLES.user_trade;
  const Icon = style.icon;

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('[role="button"]')) {
      return;
    }
    router.push(`/activity/${event.id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      onClick={handleCardClick}
      className={`${CARD_BASE} py-3.5`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-[5px] ${style.iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${style.iconColor}`} />
        </div>

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between gap-2">
            <p className="text-[13px] font-medium text-gray-900 dark:text-white line-clamp-2 flex-1">{decodeHTMLEntities(event.headline || '')}</p>
            <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">{timeAgo(event.event_at, t)}</span>
          </div>

          {/* Trade info */}
          {(event.size_usd || event.pnl_usd) && (
            <div className="flex items-center flex-wrap gap-2 mt-1">
              {event.side && (
                <span className={`text-xs font-medium ${event.side === "BUY" ? "text-emerald-500" : "text-red-500"}`}>
                  {event.side === "BUY" ? t("activity.action.bought") : t("activity.action.sold")} {event.outcome}
                  {event.price ? ` @ ${fmtCents(Number(event.price))}¢` : ''}
                </span>
              )}
              {event.size_usd && (
                <span className="text-xs font-semibold text-gray-900 dark:text-white">{formatUSD(event.size_usd)}</span>
              )}
              {event.pnl_usd && (
                <span className={`text-xs font-semibold ${event.pnl_usd > 0 ? "text-emerald-500" : "text-red-500"}`}>
                  {event.pnl_usd > 0 ? "+" : ""}{formatUSD(event.pnl_usd)}
                </span>
              )}
            </div>
          )}

          {/* Interactive market card */}
          {event.market_slug && (
            <div className="mt-2.5" onClick={(e) => e.stopPropagation()}>
              <PolymarketMarketCard slug={event.market_slug} fallbackData={{ question: event.market_title, image: event.market_image, outcomes: event.metadata?.link_preview?.outcomes, outcomePrices: event.metadata?.link_preview?.outcomePrices, active: event.metadata?.link_preview?.active, closed: event.metadata?.link_preview?.closed, endDate: event.metadata?.link_preview?.endDate, eventSlug: event.event_slug }} {...getPostAttribution(event)} />
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => onVote(event.id, 1, 'activity_event')}
                className={`flex items-center gap-1 px-1.5 py-1 rounded-[5px] text-xs transition-colors ${
                  event.user_vote === 1 ? 'text-emerald-500 bg-emerald-500/10' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-emerald-500'
                }`}
              >
                <Flame className="w-3.5 h-3.5" />
                <span>{event.reactions?.fire || event.likes || 0}</span>
              </button>
              <button
                onClick={() => onVote(event.id, -1, 'activity_event')}
                className={`flex items-center gap-1 px-1.5 py-1 rounded-[5px] text-xs transition-colors ${
                  event.user_vote === -1 ? 'text-red-500 bg-red-500/10' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-red-500'
                }`}
              >
                <Skull className="w-3.5 h-3.5" />
                <span>{event.reactions?.skull || event.dislikes || 0}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
